import { useCallback, useEffect, useRef, useState } from "react";
/** Default bottom-panel height (must match `--vnsc-bottom-panel-height` fallback in CSS). */
export const BOTTOM_PANEL_HEIGHT_PX = 260;
/** Approximate single-chart footer height used for chart-area estimation. */
export const SINGLE_FOOTER_HEIGHT_PX = 48;
/** Collapse indicator subpanes when the chart area drops below this height. */
export const COLLAPSE_CHART_AREA_PX = 300;
/**
 * UI-D1: auto-collapse indicator subpanes (single chart) while the bottom
 * panel is open and the chart area is squeezed. Restores exactly the panes
 * this hook hid (panes the user re-shows mid-collapse are left alone).
 */
export function useSingleChartPanelCollapse({ panelOpen, chartAreaHeight, collapseBelowPx = COLLAPSE_CHART_AREA_PX, visiblePaneIds, onTogglePaneVisible, }) {
    const collapsedRef = useRef(null);
    const visibleRef = useRef(visiblePaneIds);
    visibleRef.current = visiblePaneIds;
    const toggleRef = useRef(onTogglePaneVisible);
    toggleRef.current = onTogglePaneVisible;
    const shouldCollapse = panelOpen && chartAreaHeight < collapseBelowPx;
    useEffect(() => {
        if (!shouldCollapse) {
            const collapsed = collapsedRef.current;
            collapsedRef.current = null;
            if (collapsed) {
                for (const id of collapsed) {
                    if (!visibleRef.current.includes(id)) {
                        toggleRef.current(id);
                    }
                }
            }
            return;
        }
        if (collapsedRef.current) {
            return;
        }
        const ids = visibleRef.current.filter((id) => id !== "price");
        if (ids.length === 0) {
            return;
        }
        collapsedRef.current = ids;
        for (const id of ids) {
            toggleRef.current(id);
        }
    }, [shouldCollapse]);
}
/**
 * UI-D2: focus-first behavior (multi chart). Opening Pine Editor or Strategy
 * Tester auto-maximizes the active chart; closing the tab restores the grid.
 * Paper Trading keeps the grid (flexible-height panel instead).
 */
export function useMultiChartPanelFocus({ activeTab, activeChartId, }) {
    const [focusedId, setFocusedId] = useState(null);
    const manualRef = useRef(false);
    const activeChartIdRef = useRef(activeChartId);
    activeChartIdRef.current = activeChartId;
    // Start from null so mounting with an already-open tab still triggers focus.
    const prevTabRef = useRef(null);
    useEffect(() => {
        const prev = prevTabRef.current;
        prevTabRef.current = activeTab;
        if (activeTab === null) {
            manualRef.current = false;
            setFocusedId(null);
            return;
        }
        if ((activeTab === "pine_editor" || activeTab === "strategy_tester") && prev !== activeTab) {
            manualRef.current = false;
            if (activeChartId) {
                setFocusedId(activeChartId);
            }
        }
    }, [activeTab, activeChartId]);
    const setMaximizedChartId = useCallback((id) => {
        manualRef.current = true;
        setFocusedId(id);
    }, []);
    const toggleSolo = useCallback(() => {
        manualRef.current = true;
        setFocusedId((prev) => (prev !== null ? null : (activeChartIdRef.current ?? null)));
    }, []);
    return { maximizedChartId: focusedId, setMaximizedChartId, toggleSolo };
}
